// @givmo/passkeys — how one passkey reads in the Sign-in & security card (A4's words, approved):
// its name, then "Backed up" or "This device only", "Added Oct 6, 2026", and "Last used today" or
// "Last used Oct 20" (the year only when it is not this one). Dates are the reader's own calendar.
// A passkey never used since it was added says nothing about use.

import type { PasskeyOut } from '@givmo/api-client';

const FULL_DATE = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});
const SHORT_DATE = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });

/** The passkey's name, or iOS's word for an unnamed one. */
export function passkeyLabel(passkey: Pick<PasskeyOut, 'name'>): string {
  const name = passkey.name?.trim() ?? '';
  return name.length > 0 ? name : 'Passkey';
}

function parsed(value: string | null | undefined): Date | null {
  if (typeof value !== 'string' || value.length === 0) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** "Backed up · Added Oct 6, 2026 · Last used today", from what the list read carries. */
export function passkeyDetail(
  passkey: Pick<PasskeyOut, 'backed_up' | 'created_at' | 'last_used_at'>,
  now: Date,
): string {
  const parts = [passkey.backed_up ? 'Backed up' : 'This device only'];
  const added = parsed(passkey.created_at);
  if (added !== null) parts.push(`Added ${FULL_DATE.format(added)}`);
  const used = parsed(passkey.last_used_at);
  if (used !== null) {
    parts.push(
      sameDay(used, now)
        ? 'Last used today'
        : `Last used ${used.getFullYear() === now.getFullYear() ? SHORT_DATE.format(used) : FULL_DATE.format(used)}`,
    );
  }
  return parts.join(' · ');
}
